import { useQuery } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import type {
  ForecastMonthLocation,
  GetForecastDetailResponse,
  GetForecastMonthBreakdownResponse,
  GetForecastRunActualsResponse,
  GetForecastSpokeDemandResponse,
} from '@erp/rpc';

export type {
  ForecastMonthLocation,
  GetForecastDetailResponse,
  GetForecastMonthBreakdownResponse,
  GetForecastRunActualsResponse,
  GetForecastSpokeDemandResponse,
};

/** The location filter every read on the detail page shares. */
export interface ForecastDetailScopeInput {
  sku: string | undefined;
  divisionIds?: string[];
  warehouseIds?: string[];
  runId?: string;
}

export interface UseForecastDetailInput extends ForecastDetailScopeInput {
  monthFrom?: string;
  monthTo?: string;
}

/**
 * One item's forecast across a location scope, wide. Disabled until there is an
 * item — a location with no SKU is not a narrower question, it is a different
 * one, and the demand grid answers that.
 */
/**
 * What a detail read is about — the item and the location scope — as one
 * comparable string. The run and month bounds are deliberately left out: they
 * change the view of the same subject, not the subject.
 */
export function detailSubject(input: ForecastDetailScopeInput): string {
  return JSON.stringify([
    input.sku ?? '',
    [...(input.divisionIds ?? [])].sort(),
    [...(input.warehouseIds ?? [])].sort(),
  ]);
}

export function useForecastDetail(input: UseForecastDetailInput) {
  const subject = detailSubject(input);
  return useQuery<GetForecastDetailResponse>({
    ...orpc.procurement.forecastDetail.get.queryOptions({
      input: {
        sku: input.sku ?? '',
        divisionIds: input.divisionIds,
        warehouseIds: input.warehouseIds,
        runId: input.runId,
        monthFrom: input.monthFrom,
        monthTo: input.monthTo,
      },
    }),
    enabled: Boolean(input.sku),
    staleTime: 60_000,
    meta: { subject },
    // Hold the last read on screen only while the SAME item and scope load
    // under another run or month range, so the page does not empty and jump.
    // A different item or scope never shows the previous one's numbers; the
    // page marks the held read as updating (`isPlaceholderData`).
    placeholderData: (previous, previousQuery) =>
      previousQuery?.meta?.subject === subject ? previous : undefined,
  });
}

/**
 * One month of the grid, un-summed by location — the edit surface behind an
 * aggregated cell. Fetched only while a cell is open, which is why `month` is
 * nullable rather than the hook being called conditionally.
 */
export function useForecastMonthBreakdown(
  input: ForecastDetailScopeInput & {
    month: string | null;
    kind: 'history' | 'forecast';
  },
) {
  return useQuery<GetForecastMonthBreakdownResponse>({
    ...orpc.procurement.forecastDetail.monthBreakdown.queryOptions({
      input: {
        sku: input.sku ?? '',
        divisionIds: input.divisionIds,
        warehouseIds: input.warehouseIds,
        runId: input.runId,
        month: input.month ?? '',
        kind: input.kind,
      },
    }),
    enabled: Boolean(input.sku && input.month),
    // Short: a save has to be visible on reopen, and the panel is cheap.
    staleTime: 5_000,
  });
}

/**
 * What a run-actuals read is about — the item and its one location. The run
 * list is left out: adding a comparison is another view of the same subject.
 */
export function runActualsSubject(input: {
  sku: string | undefined;
  warehouseId: string | undefined;
}): string {
  return JSON.stringify([input.sku ?? '', input.warehouseId ?? '']);
}

/**
 * Earlier runs' forecasts for closed months beside the scored actuals — the
 * backward half of the run comparison. One location, like the comparison.
 */
export function useForecastRunActuals(input: {
  sku: string | undefined;
  warehouseId: string | undefined;
  runIds: string[];
}) {
  const subject = runActualsSubject(input);
  return useQuery<GetForecastRunActualsResponse>({
    ...orpc.procurement.forecastDetail.runActuals.queryOptions({
      input: {
        sku: input.sku ?? '',
        warehouseId: input.warehouseId ?? '',
        runIds: input.runIds,
      },
    }),
    enabled: Boolean(input.sku && input.warehouseId && input.runIds.length > 0),
    staleTime: 60_000,
    meta: { subject },
    // Adding a comparison keeps the rows already shown while the next loads —
    // for the same item and location only. Another item or location never
    // shows the previous one's ledger under its name.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.meta?.subject === subject ? previous : undefined,
  });
}

/**
 * The spokes the scope serves and each one's direct customer sales — under
 * Demand behaviour. Its own read because it is a different question from the
 * detail (other locations, a calendar window) and is not worth delaying it.
 */
export function useForecastSpokeDemand(input: ForecastDetailScopeInput) {
  return useQuery<GetForecastSpokeDemandResponse>({
    ...orpc.procurement.forecastDetail.spokeDemand.queryOptions({
      input: {
        sku: input.sku ?? '',
        divisionIds: input.divisionIds,
        warehouseIds: input.warehouseIds,
        runId: input.runId,
      },
    }),
    enabled: Boolean(input.sku),
    staleTime: 60_000,
  });
}
